/**
 * Store-wide campaign touches (Black Friday…), driven by Theme settings › ANJA brand › Campaign.
 * Only while the campaign is live (between start and end):
 *  - a campaign badge on product cards that have a compare-at (sale) price (badge "%" = the card's real
 *    percentage off, read from its prices),
 *  - "<label> price ends in 2d 4h" under the product page price,
 *  - the same countdown line in the cart summary,
 *  - with "Animate price drops" on: sale prices roll down from the old price when they scroll into view,
 *    on Horizon product cards and on any [data-anja-roll][data-was][data-now] element.
 */
const config = (() => {
  try {
    return JSON.parse(document.getElementById('anja-campaign')?.textContent || 'null');
  } catch {
    return null;
  }
})();

if (config && config.label) {
  const start = Date.parse(config.start);
  const end = Date.parse(config.end);
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const left = () => {
    const ms = Math.max(0, end - Date.now());
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return d > 0 ? `${d}${config.d} ${h}${config.h}` : `${h}${config.h} ${m}${config.m}`;
  };
  const live = () => !Number.isNaN(start) && !Number.isNaN(end) && Date.now() >= start && Date.now() <= end;

  // Money text → number ("LE 1,530.00" → 1530); keeps the original formatting around the digits.
  const parseMoney = (text) => Number((text || '').replace(/[^\d.,]/g, '').replace(/,/g, '')) || 0;
  const formatLike = (sample, value) => {
    const digits = sample.match(/[\d.,]+/);
    if (!digits) return String(Math.round(value));
    const hasDecimals = /[.,]\d{2}$/.test(digits[0]);
    const n = Math.round(value).toLocaleString('en-US') + (hasDecimals ? '.00' : '');
    return sample.replace(digits[0], n);
  };
  const ease = (x) => 1 - Math.pow(1 - x, 3);
  const roll = (node, from, to, ms = 650) => {
    const sample = node.textContent;
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / ms);
      node.textContent = formatLike(sample, from + (to - from) * ease(k));
      if (k < 1) requestAnimationFrame(step);
      else node.textContent = sample;
    };
    requestAnimationFrame(step);
  };
  const rollObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const el = en.target;
        rollObserver.unobserve(el);
        el.classList.add('is-dropped');
        if (reduce) return;
        const now = el.querySelector('[data-anja-roll-now]');
        const was = Number(el.dataset.was) / (el.dataset.cents ? 100 : 1);
        const to = Number(el.dataset.now) / (el.dataset.cents ? 100 : 1);
        if (now && was > to) setTimeout(() => roll(now, was, to), 120);
      });
    },
    { threshold: 0.15 }
  );

  const percentOf = (card) => {
    const was = parseMoney(card.querySelector('.compare-at-price, s')?.textContent);
    const now = parseMoney(card.querySelector('.price:not(.compare-at-price), [ref="priceContainer"] .price')?.textContent);
    return was > now && now > 0 ? Math.round(((was - now) / was) * 100) : 0;
  };

  const decorate = () => {
    if (!live()) return;
    const excluded = (type) => (config.exclude || []).some((ex) => ex.trim() && (type || '').includes(ex.trim()));
    document.querySelectorAll('product-card').forEach((card) => {
      if (excluded(card.dataset.anjaType)) return;
      const onSale = !!card.querySelector('.compare-at-price, s');
      if (!card.querySelector('.anja-camp-badge') && (config.all || onSale)) {
        const pct = (config.badge || '').trim() === '%' ? percentOf(card) : 0;
        const text = (config.badge || '').trim() === '%' ? (pct ? `−${pct}%` : '') : config.badge || config.label;
        if (text) {
          const badge = document.createElement('span');
          badge.className = 'anja-camp-badge';
          badge.textContent = text;
          (card.querySelector('.product-card__content, a') || card).prepend(badge);
        }
      }
      if (config.roll && onSale && !card.dataset.anjaRoll) {
        const price = card.querySelector('[ref="priceContainer"] .price:not(.compare-at-price), .price:not(.compare-at-price)');
        const was = parseMoney(card.querySelector('.compare-at-price, s')?.textContent);
        const now = parseMoney(price?.textContent);
        if (price && was > now) {
          card.dataset.anjaRoll = '1';
          card.dataset.was = String(was);
          card.dataset.now = String(now);
          price.setAttribute('data-anja-roll-now', '');
          rollObserver.observe(card);
        }
      }
    });
    if (config.roll) {
      document.querySelectorAll('[data-anja-roll]:not([data-anja-roll-seen])').forEach((el) => {
        el.setAttribute('data-anja-roll-seen', '');
        el.dataset.cents = '1'; // Liquid prices are in cents
        rollObserver.observe(el);
      });
    }
    const text = config.ends.replace('[label]', config.label).replace('[time]', left());
    const price = document.querySelector('.product-details product-price');
    if (price && !config.excluded_here && !document.querySelector('.anja-camp-line--pdp')) {
      const line = document.createElement('p');
      line.className = 'anja-camp-line anja-camp-line--pdp';
      price.after(line);
    }
    const summary = document.querySelector('.anja-cart-savings-slot');
    if (summary && !summary.querySelector('.anja-camp-line')) {
      const line = document.createElement('p');
      line.className = 'anja-camp-line';
      summary.append(line);
    }
    document.querySelectorAll('.anja-camp-line').forEach((l) => {
      if (l.textContent !== text) l.textContent = text;
    });
  };

  if (live() || Date.now() < start) {
    decorate();
    setInterval(decorate, 30000);
    let queued = false;
    new MutationObserver(() => {
      if (queued || !live()) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        decorate();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
}
